import { Component, OnInit } from '@angular/core';
import { combineLatest, switchMap, tap, Observable } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Tariff } from '../../tariffs/tariff.model';
import { KkmsDetailsService } from '../../../core/services/kkm-details.service';
import { TariffService } from '../../../core/services/tariff.service';
import {
  SelectTariffProcessFields,
  SelectTariffProcessForm,
} from '../sign-selected-tariff-page/sign-selected-tariff-page.model';
import { SharedModule } from '../../../shared/shared.module';
import { RetailPlaceInfoComponent } from './retail-place-info/retail-place-info.component';
import { SelectKkmTariffNavigationService } from './select-kkm-tariff-navigation.service';
import { SelectKkmTariffFormComponent } from './select-kkm-tariff-form/select-kkm-tariff-form.component';
import { TariffsModule } from '../../tariffs/tariffs.module';
import { SelectKkmTariffsListComponent } from './select-kkm-tariffs-list/select-kkm-tariffs-list.component';
import { BpService } from '../../../core/services/bp.service';
import { getRouteParam } from '../../../shared/utils/getRouteParam.utils';
import { SelectKkmTariffState } from './select-kkm-tariff.state';
import { AlertsService } from '../../../core/services/alerts.service';
import { IKkm } from '../../../core/modules/retail-places/retail-paces.types';

@UntilDestroy()
@Component({
  selector: 'tax-kkm-tariffs',
  templateUrl: './select-kkm-tariff-page.component.html',
  styleUrls: ['./select-kkm-tariff-page.component.sass'],
  imports: [
    SharedModule,
    RetailPlaceInfoComponent,
    SelectKkmTariffFormComponent,
    TariffsModule,
    SelectKkmTariffsListComponent,
  ],
  providers: [SelectKkmTariffNavigationService],
  standalone: true,
})

export class SelectKkmTariffPageComponent implements OnInit {
  selectedTariffsRange: Tariff[] = [];
  kkmName = '';
  kkmProcessFields?: SelectTariffProcessFields;
  selectedTariff?: Tariff;
  retailPlaceId?: number;
  readonly kkmId$ = getRouteParam('id');
  readonly bpStep$ = this.selectKkmTariffNavigationService.getBpStep();
  private allTariffs: Tariff[] = [];

  constructor(
    private kkmsDetailsService: KkmsDetailsService,
    private tariffService: TariffService,
    private selectKkmTariffState: SelectKkmTariffState,
    private selectKkmTariffNavigationService: SelectKkmTariffNavigationService,
    private bpService: BpService,
    private translateService: TranslateService,
    private alertsService: AlertsService,
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  /**
   *  Первый шаг - это выбор стоимости/продолжительности тарифа. Какой именно тариф надо активировать пользователю мы не знаем.
   *  Может это будет проводной (tariffId: X), а может беспроводной (tariffId: Y). Поэтому тут мы сохраняем массив тарифов, подходящих под выбранные пользователем параметры
   */
  setTariffsRange(selectedTariff: Tariff): void {
    this.selectedTariffsRange = this.tariffService.getTariffsByParams(this.allTariffs, {
      periodPrice: selectedTariff.periodPrice,
      periodUnit: selectedTariff.periodUnit,
    });
    this.selectKkmTariffNavigationService.nextBpStep();
  }

  private loadData(): void {
    this.kkmId$
      .pipe(
        switchMap(id => combineLatest([
          this.kkmsDetailsService.getKkm(id),
          this.tariffService.getKkmsTariffs(id),
        ])),
        switchMap(([kkm, tariffList]) => {
          this.processTariffData(kkm, tariffList);
          this.retailPlaceId = kkm.retailPlaceId;
          return this.runBusinessProcess();
        }),
        untilDestroyed(this),
      ).subscribe();
  }

  private processTariffData(kkm: IKkm, tariffList: Tariff[]): void {
    this.selectKkmTariffState.setTariffList(tariffList, kkm.tariffId);
    this.allTariffs = tariffList;
    this.kkmName = kkm.internalName;
  }

  private runBusinessProcess(): Observable<SelectTariffProcessForm> {
    let isNewBp = false;
    return this.bpService.observeProcessIds().pipe(
      switchMap((processIds) => {
        const { pid, tid } = processIds;
        if (pid && tid) {
          return this.bpService.getCurrentBpFormData<SelectTariffProcessForm>()
            .pipe(
              tap((formData) => {
                if (formData.alertMessage) this.addBpError(formData.alertMessage);
                this.kkmProcessFields = formData.fields;
                this.selectedTariff = this.allTariffs.find(tariff => tariff.id === Number(formData.fields.tariffId));
                if (this.selectedTariff && !isNewBp) {
                  this.selectKkmTariffNavigationService.nextBpStep();
                  this.setTariffsRange(this.selectedTariff);
                } else {
                  isNewBp = false;
                  this.selectKkmTariffNavigationService.resetBpStep();
                }
              }),
            );
        }
        isNewBp = true;
        return this.kkmId$.pipe(switchMap(kkmId => this.bpService.startBpAndGetData<SelectTariffProcessForm>(
          'kkmRegistrationProcess',
          {
            kkmId,
          },
        )));
      }),
    );
  }

  private addBpError(error: string): void {
    this.translateService.get(error)
      .pipe(
        tap(translatedError => this.addAlert(translatedError, error)),
        untilDestroyed(this),
      ).subscribe();
  }

  private addAlert(translatedError: string | any, error: string): void {
    const isNonTranslatableError = error === translatedError;
    const isEnglishOrEmptyText = !error || /[a-z]/i.test(error.charAt(0));

    const alertConfig = isNonTranslatableError && isEnglishOrEmptyText
      ? {
        message: 'alert.danger.common.text', title: 'alert.danger.common.title',
      }
      : {
        message: error,
      };

    this.alertsService.add(alertConfig);
  }
}
